import { useEffect } from 'react';
import { workspacesClient } from '../apis/client-instances';
import { User } from '../types';

// Dentlogics forms learn who the learner is through a postMessage handshake
// (the protocol TI's inline embed script spoke). Lesson HTML never runs
// scripts, so the viewer speaks it on the embed's behalf. The context carries
// learner PII, so only these origins ever receive it.
const TRUSTED_SURVEY_ORIGINS = ['https://survey.dentlogics.com'];

export interface SurveyCourse {
  slug?: string;
  title?: string;
}

interface SurveyLearner {
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  panoramaId: string;
  panoramaName: string;
  panoramaStatus: 'resolved' | 'main-site';
  courseId: string;
  courseTitle: string;
}

function originOf(src: string): string {
  try {
    return new URL(src).origin;
  } catch {
    return '';
  }
}

// Checkpoint workspaces stand in for TI panoramas; no workspace = "Main site".
async function resolveLearner(user: User | null, course?: SurveyCourse): Promise<SurveyLearner | null> {
  const firstName = user?.firstName?.trim();
  const lastName = user?.lastName?.trim();
  const email = user?.email?.trim();
  if (!firstName || !lastName || !email || !course?.slug || !course.title) return null;

  let panoramaId = '';
  let panoramaName = 'Main site';
  if (user.workspaceId) {
    const workspace = await workspacesClient.getMyWorkspaceBranding().catch(() => null);
    if (!workspace?.name) return null;
    panoramaId = String(user.workspaceId);
    panoramaName = workspace.name;
  }

  return {
    firstName,
    lastName,
    email,
    userId: String(user.id ?? ''),
    panoramaId,
    panoramaName,
    panoramaStatus: panoramaId ? 'resolved' : 'main-site',
    courseId: course.slug,
    courseTitle: course.title,
  };
}

export function useSurveyEmbedBridge(
  container: HTMLElement | null,
  html: string,
  user: User | null,
  course?: SurveyCourse,
): void {
  const courseSlug = course?.slug;
  const courseTitle = course?.title;

  useEffect(() => {
    const frames = Array.from(container?.querySelectorAll('iframe') ?? []).filter((frame) =>
      TRUSTED_SURVEY_ORIGINS.includes(originOf(frame.src)),
    );
    if (frames.length === 0) return undefined;

    let cancelled = false;
    // undefined = still resolving; null = can't identify the learner.
    let learner: SurveyLearner | null | undefined;
    // Posting before the survey page loads would target about:blank and be dropped.
    const loaded = new Set<HTMLIFrameElement>();

    const send = (frame: HTMLIFrameElement) => {
      if (learner === undefined || !loaded.has(frame) || !frame.contentWindow) return;
      const message = learner ? { type: 'dentlogics:context', learner } : { type: 'dentlogics:context-error' };
      frame.contentWindow.postMessage(message, originOf(frame.src));
    };

    const onLoad = (event: Event) => {
      const frame = event.currentTarget as HTMLIFrameElement;
      loaded.add(frame);
      send(frame);
    };

    const onMessage = (event: MessageEvent) => {
      const frame = frames.find((f) => f.contentWindow === event.source);
      if (!frame || event.origin !== originOf(frame.src) || !event.data) return;
      if (event.data.type === 'dentlogics:ready') {
        loaded.add(frame);
        send(frame);
      }
      if (event.data.type === 'dentlogics:resize' && Number.isFinite(event.data.height)) {
        frame.style.height = `${Math.max(500, Math.min(6000, Math.ceil(event.data.height)))}px`;
      }
    };

    frames.forEach((frame) => frame.addEventListener('load', onLoad));
    window.addEventListener('message', onMessage);

    resolveLearner(user, { slug: courseSlug, title: courseTitle }).then((resolved) => {
      if (cancelled) return;
      learner = resolved;
      frames.forEach(send);
    });

    return () => {
      cancelled = true;
      frames.forEach((frame) => frame.removeEventListener('load', onLoad));
      window.removeEventListener('message', onMessage);
    };
  }, [container, html, user, courseSlug, courseTitle]);
}
